import { Box, Button, Checkbox, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { colors } from "../../../ui/colors";

export interface ReminderEditorValue {
  uid?: string;
  name?: string;
  scheduleConfig: {
    type: string;
    hour: number;
    minute: number;
  };
  sendEmail: boolean;
  sendSms: boolean;
  isEnabled: boolean;
}

interface ReminderEditorProps {
  onCancel: () => void;
  onSave: (data: ReminderEditorValue) => void;
  initialData?: {
    uid?: string;
    name?: string;
    scheduleConfig?: {
      type?: string;
      hour?: number;
      minute?: number;
    };
    sendSms?: boolean;
    sendEmail?: boolean;
    isEnabled?: boolean;
  };
}

export default function ReminderEditor({
  onCancel,
  onSave,
  initialData,
}: ReminderEditorProps) {
  const [unit, setUnit] = useState<string>(initialData?.scheduleConfig?.type || "hours");
  const [hh, setHH] = useState<number>(initialData?.scheduleConfig?.hour || 0);
  const [mm, setMM] = useState<number>(initialData?.scheduleConfig?.minute || 0);
  const [sendSms, setSendSms] = useState<boolean>(initialData?.sendSms || false);
  const [sendEmail, setSendEmail] = useState<boolean>(initialData?.sendEmail || true);
  const [isEnabled] = useState<boolean>(initialData?.isEnabled ?? true);

  const inputStyle = {
    width: "65px",
    flexShrink: 0,
    "& .MuiInputBase-root": { height: "32px", fontSize: "14px" },
    "& .MuiInputBase-input": { textAlign: "center", padding: 0 },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: colors.orange.base,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
      "&.Mui-focused": { color: colors.orange.base },
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.85)",
    },
  };

  const buildReminderName = () => {
    if (unit === "hours") {
      return `Before ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")} hours`;
    }

    if (unit === "minutes") {
      return `Before ${mm} minutes`;
    }

    return `Before ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${unit}`;
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      p={1}
      sx={{
        border: "1px solid var(--fx-surface-border, #E2E8F0)",
        borderRadius: 2,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--fx-surface-textsecondary, #64748B)",
          minWidth: "45px",
          flexShrink: 0,
        }}
      >
        Before
      </Typography>

      <Box
        sx={{
          width: "138px",
          flexShrink: 0,
          display: "flex",
          gap: 1,
          justifyContent: "flex-start",
        }}
      >
        <TextField
          label="HH"
          type="number"
          value={hh}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (val >= 0 && val <= 23) setHH(val);
          }}
          onFocus={(e) => e.target.select()}
          sx={inputStyle}
        />

        <TextField
          label="MM"
          type="number"
          value={mm}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (val >= 0 && val <= 59) setMM(val);
          }}
          onFocus={(e) => e.target.select()}
          sx={inputStyle}
        />
      </Box>

      <TextField
        select
        size="small"
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        SelectProps={{ native: true }}
        sx={{
          width: "100px",
          flexShrink: 0,
          "& .MuiInputBase-root": { height: "32px", fontSize: 11 },
          "& .MuiOutlinedInput-root.Mui-focused fieldset": {
            borderColor: colors.orange.base,
          },
        }}
      >
        <option value="hours">Hours</option>
        <option value="days">Days</option>
        <option value="weeks">Weeks</option>
      </TextField>

      <Box display="flex" alignItems="center">
        <Checkbox
          size="small"
          checked={sendSms}
          onChange={(e) => setSendSms(e.target.checked)}
          sx={{
            p: 0.5,
            color: colors.gray[400],
            "&.Mui-checked": { color: colors.orange.base },
          }}
        />
        <Typography sx={{ fontSize: 10, fontWeight: 600 }}>SMS</Typography>
      </Box>

      <Box display="flex" alignItems="center">
        <Checkbox
          size="small"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          sx={{
            p: 0.5,
            color: colors.gray[400],
            "&.Mui-checked": { color: colors.orange.base },
          }}
        />
        <Typography sx={{ fontSize: 10, fontWeight: 600 }}>Email</Typography>
      </Box>

      <Box ml="auto" display="flex" gap={1} sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          onClick={onCancel}
          sx={{ color: "var(--fx-surface-textsecondary, #64748B)", textTransform: "none", fontSize: 11 }}
        >
          Cancel
        </Button>

        <Button
          size="small"
          variant="contained"
          sx={{
            bgcolor: colors.orange.base,
            "&:hover": {
              bgcolor: colors.orange[600],
              boxShadow: "none",
            },
            textTransform: "none",
            fontSize: 11,
            boxShadow: "none",
          }}
          onClick={() =>
            onSave({
              uid: initialData?.uid,
              name: buildReminderName(),
              scheduleConfig: {
                type: unit,
                hour: hh,
                minute: mm,
              },
              sendEmail,
              sendSms,
              isEnabled,
            })
          }
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}